import type { PostedAsOrg } from "@/lib/organizations";
// Staffing/headhunter admin types live in their own module; re-exported here so
// existing `@/lib/types` imports keep resolving.
export * from "./staffingTypes";

import type { QuestScene } from "./questArt/scene";
import type { TaskTrustFields, TrustFactors, TrustFactorsSource, TrustGrade } from "@/lib/trustGrade/types";
import type { ContentI18n, ContentLocale } from "./questContent/types";
import type { MessageReaction } from "./workspace/reactions";

// ─── Currency ───────────────────────────────────────────

/** Tokens allowed for new operations (quest creation, funding, payments). */
export const SUPPORTED_CURRENCIES = ['USDT'] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/** All tokens the platform can display and interact with (balances, address resolution, legacy escrows). */
export const KNOWN_CURRENCIES = ['USDC', 'USDT'] as const;
export type KnownCurrency = (typeof KNOWN_CURRENCIES)[number];

export function isSupportedCurrency(value: unknown): value is SupportedCurrency {
  return typeof value === 'string' &&
    (SUPPORTED_CURRENCIES as readonly string[]).includes(value);
}

export function isKnownCurrency(value: unknown): value is KnownCurrency {
  return typeof value === 'string' &&
    (KNOWN_CURRENCIES as readonly string[]).includes(value);
}

// ─── Quest ──────────────────────────────────────────────
export type QuestStatus = "draft" | "published" | "negotiating" | "locking" | "active" | "completed" | "closed" | "cancelled";
/**
 * A candidate's position in the quest funnel. The sourcing side
 * ('suggested'/'reviewed'/'invited'/'dismissed') is written by the Headhunter
 * worker and admins; the worker-facing side ('applied'/'selected') is what the
 * person themselves and the creator drive. See hasAppliedToQuest in questStatus.
 */
export type QuestCandidateStatus =
  | "suggested"
  | "reviewed"
  | "dismissed"
  | "invited"
  | "applied"
  | "selected";

export type TaskType = "human" | "ai";
export type WorkMode = "remote" | "onsite" | "hybrid";
export type TaskStatus =
  | "draft"
  | "open"
  | "assigned"
  | "in_progress"
  | "submitted"
  | "approved"
  | "changes_requested"
  | "completed"
  | "disputed"
  | "cancelled";
export type BountyStatus =
  | "open"
  | "assigned"
  | "in_progress"
  | "submitted"
  | "approved"
  | "completed"
  | "disputed";

export interface Quest {
  id: string;
  creatorId: string;
  title: string;
  description: string;
  category: string;
  status: QuestStatus;
  totalBudgetUSD: number;
  currency: KnownCurrency;
  locationRequirement: "remote" | "onsite";
  deadline: string | null;
  attachments: Attachment[];
  createdAt: string;
  updatedAt: string;
  taskCount: number;
  completedTaskCount: number;
  chainQuestId?: number | null;
  chainTxHash?: string | null;
  /** DEV-824: machine-written translations of `title`/`description`, resolved
   *  client-side so the language toggle is a re-render, not a refetch.
   *  Optional because the admin/mock adapters predate the columns; an absent
   *  value resolves to the creator's own words, same as an empty object. */
  i18n?: ContentI18n | null;
  sourceLang?: string | null;
  templateId?: string | null;
  /**
   * DEV-318: true only on the quest that carries a stock quest's public
   * listing. Clones minted per accepted worker carry `templateId` too, so
   * `templateId` alone cannot tell a listing from an instance — and routing
   * the accept flow off it sent clones into the clone RPC and ordinary
   * accepts at the anchor, which collapsed 1-creator-many-workers to 1:1.
   */
  isTemplateAnchor?: boolean;
  /** The org this quest was posted as (20261008100000); null = the person. */
  organizationId?: string | null;
}

// ─── Quest Template ──────────────────────────
export type TemplateStatus = "draft" | "open" | "filled" | "closed";

export interface TaskDefinition {
  title: string;
  description?: string;
  taskType?: TaskType;
  estimatedEffortHours?: number;
  skills?: string[];
  /** Where every slot's task happens. Read by create_quest_template and
   *  clone_quest_from_template; omitted means remote with no place. */
  workMode?: WorkMode;
  locationCountries?: string[];
  locationCities?: string[];
  trustFactors?: TrustFactors;
  trustFactorsSource?: TrustFactorsSource;
}

export interface QuestTemplate {
  id: string;
  creatorId: string;
  title: string;
  description: string;
  category: string;
  budgetPerQuest: number;
  currency: KnownCurrency;
  deadline: string | null;
  taskDefinition: TaskDefinition;
  maxSlots: number;
  filledSlots: number;
  status: TemplateStatus;
  anchorQuestId: string | null;
  chainId: number;
  createdAt: string;
  updatedAt: string;
  /** The listing's translations. A template row has no `i18n` of its own —
   *  translate-content writes to its anchor quest — so these are the anchor's,
   *  carried only while the anchor's text still matches the template's (a
   *  translation of other words must not stand in for these). Optional for
   *  the same reason as `Quest.i18n`. */
  i18n?: ContentI18n | null;
  sourceLang?: string | null;
}

/**
 * The worker a stock-quest instance was minted for. Every field but `id` is
 * nullable: it is resolved through `public_profiles`, and a profile that can't
 * be read must still leave the instance (and its worker's id) on the list.
 */
export interface TemplateInstanceWorker {
  id: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
}

/**
 * One per-worker quest cloned from a stock quest. A clone has a single task,
 * so it has at most one worker — null only for a task nobody holds.
 */
export interface TemplateInstance extends Quest {
  worker: TemplateInstanceWorker | null;
  /** The clone's single task's status. The quest stays "active" while the
   *  task moves through assigned → submitted, so only this says the worker
   *  has handed in work that is waiting on the creator. */
  taskStatus: TaskStatus | null;
}

export interface Task extends Partial<TaskTrustFields> {
  id: string;
  questId: string;
  parentTaskId: string | null;
  title: string;
  description: string;
  taskType: TaskType;
  status: TaskStatus;
  budgetUSD: number;
  estimatedEffortHours: number;
  deadline: string | null;
  skills: string[];
  workMode: WorkMode;
  locationCountries: string[];
  locationCities: string[];
  assigneeId: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
  chainTaskId?: number | null;
  chainTxHash?: string | null;
  chainAssigned?: boolean;
  /** DEV-824: machine-written translations of `title`/`description`, resolved
   *  client-side so the language toggle is a re-render, not a refetch. Same
   *  shape and same reasoning as `Quest.i18n`; optional because the wizard and
   *  mock adapters build tasks that never came from a row. */
  i18n?: ContentI18n | null;
  sourceLang?: string | null;
}

export interface TaskTreeNode extends Task {
  children: TaskTreeNode[];
}

// ─── Bounty (a Task from the doer's perspective) ────────
export interface BountyCard {
  id: string;
  taskTitle: string;
  taskDescription: string;
  taskType: TaskType;
  questTitle: string;
  questId: string;
  /** Canonical filter category, derived — never the raw `quests.category` column. */
  category: string;
  /**
   * Which picture the card draws, derived alongside `category`.
   *
   * It has to travel separately, because `category` is LOSSY for art: the
   * filter only offers eight categories, so `photography` and `errands` both
   * narrow to `other`. A view that tried to recover the scene from `category`
   * got `other` back — `deriveQuestScene` trusts a known category, so it never
   * reached the skills — and every photo quest rendered the generic toolbox
   * again. Deriving both at the same seam, from the same row, is what keeps
   * them consistent.
   */
  artScene: QuestScene;
  /**
   * What ONE worker is paid for the job this card stands for.
   *
   * On a quest-level card (the discover feed) that is the pay of the CHEAPEST
   * open task, not `quests.total_budget_usd`. The total is what the creator
   * escrows across every task; quoting it told a worker looking at a 100+200+300
   * quest that they would be paid 600 for a 100 job.
   */
  budgetUSD: number;
  /**
   * The top of that range when the card stands for several differently priced
   * open tasks. Equal to `budgetUSD` on a single-task card, and absent on
   * surfaces that describe exactly one task.
   */
  budgetMaxUSD?: number;
  /** Most sensitive trust grade among the tasks this card stands for (0 = Special). */
  trustGrade?: TrustGrade | null;
  /** Least sensitive one; equal to trustGrade when they all match. */
  trustGradeLeast?: TrustGrade | null;
  /** Single-task surfaces only: the factors behind the grade, for "Why this grade". */
  trustFactors?: TrustFactors;
  /** Single-task surfaces only, like trustFactors. List cards leave it undefined. */
  trustGradeOverridden?: boolean;
  currency: KnownCurrency;
  estimatedEffortHours: number;
  deadline: string | null;
  /**
   * DEV-794: when the QUEST was posted (`quests.created_at`) — the quest, not
   * the task, because the card stands for the listing a worker is deciding on.
   * Optional so a producer that cannot see it omits the "Posted" line rather
   * than inventing a date.
   */
  questCreatedAt?: string | null;
  skills: string[];
  /**
   * DEV-781: null when the creator profile carries no display name. Never a
   * fabricated placeholder — every surface omits the creator element rather
   * than asserting a person called "Unknown".
   */
  creatorName: string | null;
  /** DEV-781: free-text business blurb from profiles.bio; null when absent or unreadable. */
  creatorBlurb: string | null;
  creatorAvatarUrl: string | null;
  /**
   * The org the quest was posted as (20261008100000). When set, cards show
   * the org as the poster and the creator as "via"; null = posted as a person.
   * Optional so producers that predate the column simply omit it.
   */
  postedAsOrg?: PostedAsOrg | null;
  /**
   * `quests.creator_id`, carried so a surface can read the creator's on-chain
   * reputation (CreatorReputation). There is deliberately no score field here:
   * the one that lived here read `public_profiles.reputation_overall`, which
   * nothing writes, so every card said "0 rep".
   */
  creatorId: string | null;
  /** DEV-824: the task's stored translations, resolved client-side so the
   *  language toggle is a re-render rather than a refetch. */
  i18n: ContentI18n;
  sourceLang: string | null;
  /** The quest heading shown as the card's context line has its own row, so it
   *  carries its own translations. */
  questI18n: ContentI18n;
  questSourceLang: string | null;
  applicantCount: number;
  maxSlots?: number | null;
  filledSlots?: number | null;
  /**
   * How many DISTINCT tasks in this quest are still open (discover feed only).
   * Deliberately not called "slots": a stock quest's slots are N identical
   * openings priced per worker (see `maxSlots`/`filledSlots`), whereas these
   * are N different jobs under one escrow. One word for both left a worker
   * unable to tell "I can be one of three" from "this is three jobs".
   */
  openTaskCount?: number;
  /** IDs of open tasks within this quest (used for swipe filtering) */
  openTaskIds?: string[];
  /** Strictest work mode across open tasks (onsite > hybrid > remote). */
  workMode?: WorkMode;
  /** Union of ISO 3166-1 alpha-2 country codes across open tasks. */
  locationCountries?: string[];
  /** Union of stored (normalised) city names across open tasks. */
  locationCities?: string[];
}

export interface BountyDetail extends BountyCard {
  fullDescription: string;
  attachments: Attachment[];
  requirements: string[];
  parentTaskContext: TaskTreeNode[];
  status: BountyStatus;
  assigneeId: string | null;
  /** My Work timeline: when the latest submission went in, from getMyBounties only. */
  submittedAt?: string | null;
  /** My Work timeline: tasks.updated_at, read as "finished at" once approved/completed. */
  updatedAt?: string | null;
}

// ─── Roles ─────────────────────────────────────────────
/** Access-control role (NOT the same as rolePreference which is a UX preference) */
export type UserRole = "admin" | "user";

/**
 * How much of the app the user sees (profiles.tier). Not an access role:
 * admins are tier #1 by `role`, whatever this says. See src/lib/tier.ts.
 */
export type UserTier = "standard" | "pro";

// ─── User ───────────────────────────────────────────────
export type ActivityLevel = "casual" | "active" | "core";
export type RolePreference = "creator" | "hunter" | "both";
export type SupportedLanguage = ContentLocale;

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ["en", "es", "pt"] as const;

/**
 * The single en/es/pt guard for the browser bundle.
 *
 * The literal was written out three times -- twice byte-identically in the
 * profile adapters and once as the store's array -- so a fourth locale meant
 * finding all three. The worker and the Deno edge function keep their own
 * copies: they are separate build targets with no sharing mechanism, which is
 * a real boundary rather than duplication anyone can remove.
 */
export function toSupportedLanguage(value: unknown): SupportedLanguage | null {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
    ? (value as SupportedLanguage)
    : null;
}

/**
 * 20260923120000: a worker's explicit answer to "want more quests like this?".
 * `wantsMatching` is a matching preference (for this kind it overrides
 * `UserProfile.recurringMatch`); `wantsAlerts` is an opt-in to admin-sent
 * member notifications. Neither is a contact permission for outreach.
 */
export type QuestInterestKind = "volunteer";

export interface QuestInterest {
  kind: QuestInterestKind;
  wantsAlerts: boolean;
  wantsMatching: boolean;
  /** Where they answered; null when set from Profile. */
  sourceQuestId: string | null;
  answeredAt: string;
  updatedAt: string;
}

export interface SetQuestInterestInput {
  kind: QuestInterestKind;
  wantsAlerts: boolean;
  wantsMatching: boolean;
  /** The completed volunteer quest the prompt was shown on; omit from Profile. */
  sourceQuestId?: string | null;
}

/**
 * 20261020100000: a worker's one-tap answer to "Interested in quests like
 * this?" on one quest. A ranking preference only — never a contact
 * permission. The features are the quest's, snapshotted server-side when they
 * answered, so the preference profile needs no read of the quest itself.
 */
export type QuestAffinityAnswer = "yes" | "no";

export interface QuestAffinity {
  questId: string;
  answer: QuestAffinityAnswer;
  /** quests.category at answer time, raw — derive the canonical one with skills. */
  category: string;
  skills: string[];
  isVolunteer: boolean;
  updatedAt: string;
}

export interface UserProfile {
  id: string;
  handle: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  /** Stored object path in profile-images, always under `{id}/` (20261001100000). */
  bannerPath?: string | null;
  /** Public URL of `bannerPath`, resolved by the adapter. Null = default gradient. */
  bannerUrl?: string | null;
  bio: string;
  skills: string[];
  needs: string[];
  walletAddress: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  rolePreference: RolePreference;
  /**
   * DEV-783 step 9: the worker is open to being matched to similar work in
   * future. Default true, set from the toggle inside the payout confirmation.
   *
   * NOT a contact permission — consent to contact lives in
   * quest_candidates.trigger_consent_basis (outreach) and notification_routes
   * (members). The name invites that confusion, so it is named here too.
   */
  recurringMatch: boolean;
  activityLevel: ActivityLevel;
  preferredCurrency: SupportedCurrency;
  preferredLanguage: SupportedLanguage | null;
  reputation: ReputationScore;
  role: UserRole;
  /** profiles.tier, read where an admin needs it; undefined renders as medium. */
  tier?: UserTier;
  /** When an admin last set tier, and who (display name), for AdminUserDetail. */
  tierSetAt?: string | null;
  tierSetByName?: string | null;
  createdAt: string;
}

export interface PublicProfile {
  /** Stable profile id (Privy DID). Handles can change; reads keyed to this can't drift. */
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  /** Public URL of the member's banner (public_profiles.banner_path, 20261001120000). Null = default gradient. */
  bannerUrl: string | null;
  bio: string;
  skills: string[];
  /** What they are on weXare for (public_profiles.role_preference): the header's "Open to work" / "Posts quests". */
  rolePreference: RolePreference;
  reputation: ReputationScore;
  completedBounties: number;
  questsCreated: number;
  memberSince: string;
  /** public_profiles.account_type (20261011110000). Missing column (pre-migration) reads as human. */
  accountType: "human" | "agent";
  /** The human who owns this agent (public_profiles.agent_owner_id); null for humans. */
  agentOwnerId: string | null;
}

/**
 * A member's off-chain track record, from `get_profile_track_record`. Counts
 * only — built from approved submissions on other people's quests, so none of
 * it is self-awarded. Scoring lives in `src/lib/trackRecord.ts`.
 */
export interface TrackRecord {
  worker: {
    paidTasks: number;
    volunteerTasks: number;
    paidHours: number;
    volunteerHours: number;
    questsJoined: number;
    creatorsServed: number;
  };
  creator: {
    questsLaunched: number;
    /** Launched quests where another member's work was approved — what the
     *  creator score and badge count, so publishing empty quests moves nothing. */
    questsDelivered: number;
    questsCompleted: number;
    paidQuests: number;
    volunteerQuests: number;
    tasksApproved: number;
    workersHired: number;
  };
  /** One entry per approved task: its canonical (untranslated) skills. */
  taskSkills: string[][];
  /**
   * Non-zero days only, `YYYY-MM-DD` (UTC), last 371 days. `worked` =
   * `paid + volunteer`; before migration 20260927150000 the RPC sent `worked`
   * alone, and the adapter reads the missing split as 0.
   */
  activity: { day: string; worked: number; paid: number; volunteer: number; launched: number }[];
}

/**
 * The five weighted axes, on a 0–100 scale.
 *
 * NOT the contract's scale: `ReputationLedger` stores basis points (0–10000).
 * `formatReputationScore` converts at the client boundary — see
 * `src/lib/reputationScale.ts`. Anything constructing this type by hand (mocks,
 * fixtures) must use 0–100 too, or it will not reproduce what real data does.
 */
export interface ReputationScore {
  overall: number;
  completionRate: number;
  qualityScore: number;
  speedScore: number;
  reliability: number;
}

/**
 * A full on-chain reputation read: the axes plus the activity counters.
 *
 * The counters matter more than they look. `recordTaskCompletion` increments
 * `tasksCompleted` *before* reading it as the EMA smoothing factor, so the
 * first completion runs `_ema(5000, 10000, 1)` and saturates outright —
 * Completion, Quality and Reliability are 50 before a worker's first approved
 * task and 100 after, with nothing in between. The counters are the only part
 * of this struct that keeps carrying signal as history accumulates, which is
 * why the profile card leads with them.
 */
export interface ReputationDetail extends ReputationScore {
  tasksCompleted: number;
  tasksDisputed: number;
  questsCreated: number;
  questsCompleted: number;
  /**
   * Unix seconds of the last ledger write. Zero means the address was never
   * passed to `initializeReputation` — i.e. never registered on this chain.
   *
   * This is the ONLY reliable "nothing here" discriminator. A registered user
   * with no activity reads 5000 on every axis (= 50), not 0, so an all-zero
   * score cannot be used to mean "new user".
   */
  lastUpdated: number;
}

// ─── Auth ───────────────────────────────────────────────
export interface AuthSession {
  userId: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  walletAddress: string | null;
  role: UserRole;
  /** profiles.tier; undefined until the profile sync answers (renders as simple until synced). */
  tier?: UserTier;
  createdAt: string;
}

// ─── Submissions ────────────────────────────────────────
export interface WorkSubmission {
  id: string;
  taskId: string;
  submitterId: string;
  description: string;
  attachments: Attachment[];
  submittedAt: string;
  status: "pending" | "approved" | "changes_requested";
  feedback: string | null;
}

// ─── Payments ───────────────────────────────────────────
export interface EscrowRecord {
  id: string;
  questId: string;
  totalAmount: number;
  currency: KnownCurrency;
  status: "funded" | "partially_released" | "fully_released" | "disputed";
  createdAt: string;
}

export interface Transaction {
  id: string;
  type: "escrow_fund" | "payment_release" | "refund";
  amount: number;
  currency: KnownCurrency;
  fromAddress: string;
  toAddress: string;
  txHash: string | null;
  status: "pending" | "confirmed" | "failed";
  createdAt: string;
}

export interface WalletBalance {
  address: string;
  balances: { currency: KnownCurrency; amount: number }[];
}

// ─── Shared ─────────────────────────────────────────────
export interface Attachment {
  id: string;
  fileName: string;
  fileType: string;
  url: string;
  sizeBytes: number;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  cursor: string | null;
  hasMore: boolean;
}

export interface Notification {
  id: string;
  userId: string;
  type:
    | "bounty_accepted"
    | "submission_received"
    | "payment_released"
    | "quest_completed"
    | "new_bounty_match"
    | "changes_requested"
    | "proposal_received"
    | "proposal_accepted"
    | "proposal_rejected"
    | "counter_proposal_received"
    | "proposal_superseded"
    | "proposal_withdrawn"
    | "quest_funds_locked"
    | "candidates_ready"
    | "candidates_match"
    // Admin-only: written for every profiles.role='admin' user when ANY user
    // creates a quest (supabase/functions/quest-notify/adminAlert.ts).
    | "quest_created_admin"
    // Admin-sent volunteer invitation (admin_invite_member_to_volunteer,
    // 20260922140000). Links to the task, where the member taps Join.
    | "volunteer_invite"
    // Admin-sent alert to members who asked for volunteer quests
    // (admin_send_volunteer_quest_alert, 20260923120000). Links to the quest.
    | "volunteer_quest_alert"
    // Workspace chat (notify_on_workspace_message, 20260924110000). Group
    // posts (task_id IS NULL) are workspace_group_message; task-thread posts
    // are workspace_message_received. Links to /assistant/w/<quest>?thread=…
    | "workspace_message_received"
    | "workspace_group_message"
    // Organizations (20261007100000). Each names the org in quotes in a fixed
    // English shape that getLocalizedMessage parses. Links to /o/<slug>.
    | "org_invite"
    | "org_join_request"
    | "org_join_decided"
    | "org_role_changed"
    // A certificate still waiting for the creator's review
    // (certificate_review_pending_notify, 20261021170000). In-app only; links
    // to the task page at ?review=<certificate id>.
    | "certificate_review_pending"
    // Skill certification issued (20261028140000). In-app only; links to
    // /profile?tab=skills.
    | "skill_certified"
    // Daily "what needs you" summary (attention-digest edge fn, 20261007130000).
    // One per user per UTC day; title/message written in the recipient's
    // language; links to /dashboard.
    | "attention_digest";
  title: string;
  message: string;
  linkTo: string;
  read: boolean;
  createdAt: string;
}

// ─── AI ─────────────────────────────────────────────────
export interface AIDecompositionResult {
  suggestedTasks: SuggestedTask[];
  reasoning: string;
  confidence: number;
}

export interface SuggestedTask {
  title: string;
  description: string;
  taskType: TaskType;
  taskTypeReasoning: string;
  estimatedEffortHours: number;
  suggestedBudgetUSD: number;
  skills: string[];
  subtasks?: SuggestedTask[];
  order: number;
}

/**
 * Client-side task shape used while a quest is being composed (previously by
 * the wizard UI, now by the agent-driven bounty bridge). Maps to CreateTaskInput
 * before it reaches IQuestService.
 */
export interface WizardTask {
  trustFactors?: TrustFactors;
  trustFactorsSource?: TrustFactorsSource;
  id: string;
  parentId: string | null;
  title: string;
  description: string;
  taskType: TaskType;
  taskTypeReasoning: string;
  estimatedEffortHours: number;
  suggestedBudgetUSD: number;
  skills: string[];
  order: number;
  children: WizardTask[];
  collapsed: boolean;
  workMode?: "remote" | "onsite" | "hybrid";
  locationCountries?: string[];
  locationCities?: string[];
}

// ─── Service Inputs ─────────────────────────────────────
export interface CreateQuestInput {
  title: string;
  description: string;
  category: string;
  totalBudgetUSD: number;
  currency: SupportedCurrency;
  deadline?: string | null;
  tasks: CreateTaskInput[];
  attachments?: File[];
  /** "Post as" an org the creator belongs to; omitted/null = as yourself. */
  organizationId?: string | null;
}

export interface CreateTaskInput {
  trustFactors?: TrustFactors;
  trustFactorsSource?: TrustFactorsSource;
  title: string;
  description: string;
  taskType: TaskType;
  estimatedEffortHours: number;
  skills: string[];
  workMode?: WorkMode;
  locationCountries?: string[];
  locationCities?: string[];
  budgetUSD?: number;
  deadline?: string | null;
  order?: number;
  subtasks?: CreateTaskInput[];
}

export interface QuestFilters {
  status?: QuestStatus;
  cursor?: string;
  limit?: number;
}

export type DiscoverSortBy =
  | "newest"
  | "budget_asc"
  | "budget_desc"
  | "deadline_asc";

export interface DiscoverParams {
  userId?: string;
  search?: string;
  sortBy?: DiscoverSortBy;
  skills?: string[];
  categories?: string[];
  budgetMin?: number;
  budgetMax?: number;
  taskType?: TaskType;
  locationCountry?: string;
  locationCity?: string;
  workMode?: WorkMode;
  cursor?: string;
  limit?: number;
}

export interface DecomposeContext {
  category?: string;
  existingTasks?: string[];
}

export interface EffortEstimate {
  hours: number;
  confidence: number;
}

export interface RankedBounty {
  bountyId: string;
  matchScore: number;
}

export interface TransactionResult {
  txHash: string;
  status: "pending" | "confirmed" | "failed";
}

export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  reputation: number;
  completedBounties: number;
}

// ─── Admin ─────────────────────────────────────────────
export interface AdminWorkItemKPIs {
  totalQuests: number;
  activeQuests: number;
  totalTasks: number;
  openTasks: number;
  unassignedTasks: number;
  staleTasks: number;
  pendingReviewTasks: number;
  disputedTasks: number;
  completionRate: number;
  totalUsers: number;
  activeUsers: number;
}

/** One row of admin_list_agents: an agent account with its owner. */
export interface AdminAgentSummary {
  agentId: string;
  agentDid: string;
  name: string;
  status: "active" | "paused" | "revoked";
  ownerId: string;
  ownerName: string;
  createdAt: string;
  lastActivityAt: string | null;
}

export interface AdminUserSummary {
  id: string;
  displayName: string;
  handle: string;
  email: string;
  avatarUrl: string | null;
  rolePreference: RolePreference;
  role: UserRole;
  createdAt: string;
  questCount: number;
  taskCount: number;
  proposalCount: number;
  /**
   * Quests this person applied to on the selected chain.
   *
   * The count the directory was missing: since `worker_apply_to_quest`, an
   * application is a `quest_candidates` row rather than a proposal, so a worker
   * who arrived through "I'm interested" reads as 0/0/0 on the other three.
   */
  applicationCount: number;
  /**
   * Which door this person came in through, or null if they signed up on their
   * own. `leads.source` for a claimed lead; "outreach" for someone the
   * headhunter converted before `public.leads` existed.
   */
  leadSource: LeadSource | null;
  /** When they claimed the lead. Null for a direct signup. */
  leadClaimedAt: string | null;
  /**
   * profiles.tier, so the list can switch it inline. Undefined when it could
   * not be read (the list still renders; the row shows no switch).
   */
  tier?: UserTier;
}

export interface AdminUserDetail {
  profile: UserProfile;
  walletAddress: string | null;
  chainRegistered: number | null;
  isOnboarded: boolean;
  totalBudgeted: number;
  totalPaid: number;
  totalEarned: number;
}

export interface AdminNotificationChannel {
  id: string;
  provider: string;
  displayIdentifier: string | null;
  optedIn: boolean;
  verifiedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AdminChannelMutationResult =
  | "revoked"
  | "restored"
  | "already_revoked"
  | "already_active"
  | "revoked_by_other"
  | "not_admin_revoked"
  | "active_conflict"
  | "address_changed"
  | "state_changed"
  | "not_found";

/**
 * How a profile got onto the platform.
 *
 * `lead` = we hold (or held) a lead for them and they claimed it, so someone
 * went and found this person; `signup` = they arrived on their own. See
 * `LeadSource` for which door a lead came through.
 */
export type AdminUserOrigin = "all" | "lead" | "signup";

/** `leads.source`, plus the pre-leads-table outreach converts. */
export type LeadSource = "candidate" | "manual" | "csv" | "outreach";

export interface AdminUserFilters {
  chainId?: number;
  search?: string;
  noLocation?: boolean;
  cursor?: string;
  limit?: number;
  /** Defaults to "all". */
  origin?: AdminUserOrigin;
  /** Narrows `origin: "lead"` to one door; ignored otherwise. */
  leadSource?: LeadSource | null;
  /**
   * Only people with some activity (quest, task, proposal or application) on
   * `chainId`. Profiles themselves have no chain, so without this the list is
   * the same on Testnet and Mainnet.
   */
  activeOnly?: boolean;
  /** Only this experience tier. Applied by the RPC; ignored on the no-location view. */
  tier?: UserTier;
}

export interface AdminQuestFilters {
  chainId?: number;
  status?: QuestStatus;
  search?: string;
  noLocation?: boolean;
  cursor?: string;
  limit?: number;
}

export interface AdminTaskFilters {
  trustGrade?: TrustGrade;
  specialSuggestedOnly?: boolean;
  trustSource?: TrustFactorsSource;
  chainId?: number;
  status?: TaskStatus;
  taskType?: TaskType;
  assigneePresence?: "assigned" | "unassigned" | "all";
  staleOnly?: boolean;
  cursor?: string;
  limit?: number;
}

// ─── Toast ──────────────────────────────────────────────
export interface Toast {
  id: string;
  type: "success" | "error" | "info" | "warning";
  message: string;
  /**
   * Lifetime override in ms; defaults to `LIMITS.TOAST_DURATION_MS`.
   *
   * The 4s default suits a one-line confirmation. It is far too short for a
   * message the reader has to act on — the unconfirmed-send warning is ~200
   * characters and wraps to four lines, so it was disappearing before it could
   * be finished.
   */
  durationMs?: number;
  /** One in-app link under the message, e.g. the next step after a celebration. */
  action?: { label: string; to: string };
}

// ─── Onboarding ─────────────────────────────────────────
export interface OnboardingData {
  step: number;
  rolePreference: RolePreference;
  commitmentAccepted: boolean;
  activityLevel: ActivityLevel;
  skills: string[];
  needs: string[];
  simulationResponse: "contribute" | "post" | null;
}

export interface PlatformStats {
  activeContributors: number;
  tasksCompletedThisWeek: number;
  totalPaymentsReleased: number;
}

export interface SampleTask {
  title: string;
  description: string;
  budget: number;
  aiHumanSplit: string;
  estimatedTime: string;
}

// ─── Negotiating Bounties (worker's active negotiations) ─
/**
 * A task the worker has put their hand up for but has not been given: a legacy
 * swipe-right, or an application made through one-tap express interest. Not a
 * `BountyDetail` — nothing here is assigned, so most of that shape is absent.
 */
export interface InterestedBounty {
  taskId: string;
  questId: string;
  taskTitle: string;
  questTitle: string;
  /** DEV-824: the task's and the quest's machine-written translations. */
  i18n: ContentI18n | null;
  sourceLang: string | null;
  questI18n: ContentI18n | null;
  questSourceLang: string | null;
  budgetUsd: number;
  /** DEV-781: the quest's settlement token, so prices render "200 USDT" not "$200". */
  currency: KnownCurrency;
  /** The TASK's status, not the application's. */
  status: string;
  appliedAt: string;
}

export interface NegotiatingBounty {
  taskId: string;
  questId: string;
  taskTitle: string;
  questTitle: string;
  taskType: TaskType;
  budgetUsd: number;
  /** DEV-781: the quest's settlement token, so prices render "200 USDT" not "$200". */
  currency: KnownCurrency;
  myLatestProposal: PriceProposal;
  negotiationStatus: "awaiting_response" | "counter_received" | "accepted";
  /** DEV-824: the task row's own translations. */
  i18n?: ContentI18n | null;
  sourceLang?: string | null;
  /** The quest heading is a separate row, so it carries separate translations. */
  questI18n?: ContentI18n | null;
  questSourceLang?: string | null;
}

// ─── Negotiation / Proposals ───────────────────────────
export type ProposalStatus = "pending" | "accepted" | "rejected" | "withdrawn" | "superseded" | "countered";

export interface PriceProposal {
  id: string;
  taskId: string;
  questId: string;
  workerId: string;
  proposedPrice: number;
  message: string | null;
  status: ProposalStatus;
  parentProposalId: string | null;
  round: number;
  proposedBy: "worker" | "creator";
  createdAt: string;
  updatedAt: string;
}

export interface NegotiationThread {
  taskId: string;
  workerId: string;
  /**
   * The worker's display name or @handle, or null when their public profile is
   * unreadable. NEVER the Privy DID — callers render a translated generic for
   * null. Nullable on purpose: a `string` here is what let the id leak in.
   */
  workerName: string | null;
  workerAvatarUrl: string | null;
  proposals: PriceProposal[];
  currentStatus: ProposalStatus;
  latestPrice: number;
  agreedPrice: number | null;
}

export interface TaskProposalSummary {
  taskId: string;
  /** The task's SOURCE title. Readers see `i18n[locale]` when it exists —
   *  resolve with `resolveContent`, never render this directly. */
  taskTitle: string;
  askingPrice: number;
  totalProposals: number;
  pendingProposals: number;
  acceptedWorkerId: string | null;
  acceptedWorkerName: string | null;
  agreedPrice: number | null;
  /** DEV-824. Carried, not resolved: the adapter cannot know the reader's
   *  language, and resolving in the query makes the chrome flip a round-trip
   *  before the content does. See `useQuestContent`. */
  i18n: ContentI18n | null;
  sourceLang: string | null;
  /** Does this task satisfy the funding gate? Computed with the SERVER's
   *  predicate (`lockFundsAndStart`), which exempts $0 tasks — they need no
   *  escrow, so they cannot block it. Clients MUST use this rather than
   *  re-deriving from `agreedPrice`: a hand-rolled `agreedPrice !== null`
   *  disagrees on exactly the $0 case and permanently disables a button the
   *  service would have honoured. */
  isStaffed: boolean;
}

// ─── Workspace ──────────────────────────────────────────
export type WorkspaceMemberRole = "creator" | "worker";
export type WorkspaceMessageType =
  | "text"
  | "system"
  | "status_update"
  | "attachment"
  | "submission";
export type WorkspaceSenderRole = "creator" | "worker" | "assistant" | "system";

export interface WorkspaceMember {
  id: string;
  questId: string;
  userId: string;
  role: WorkspaceMemberRole;
  joinedAt: string;
  displayName?: string;
  avatarUrl?: string | null;
}

export interface WorkspaceMessage {
  id: string;
  questId: string;
  senderId: string | null;
  senderRole: WorkspaceSenderRole;
  content: string;
  messageType: WorkspaceMessageType;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  /** NULL = the quest-wide General thread. */
  taskId: string | null;
  // Joined fields (not in DB, resolved on fetch)
  senderDisplayName?: string;
  senderAvatarUrl?: string | null;
  /** Joined on fetch. Absent means not loaded; empty array means none. */
  reactions?: MessageReaction[];
}

export interface WorkspaceListEntry {
  questId: string;
  questTitle: string;
  questStatus: QuestStatus | TaskStatus;
  role: WorkspaceMemberRole;
  joinedAt: string;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  /** True only for a stock listing's anchor quest — its workspace IS the
   *  listing's Group chat, not a clone's private thread. Defaults false. */
  isTemplateAnchor: boolean;
  /** From get_workspace_inbox (20260925100000). The other side's display name
   *  when exactly one person holds the other role; absent before the
   *  migration is applied. */
  counterpartName?: string | null;
  /** How many members hold the other role (worker → creators, creator → workers). */
  counterpartCount?: number;
  /** message_type of the last visible message ('system' renders italic). */
  lastMessageType?: string | null;
  /** metadata.i18n_key of a system last message — localized via an allowlist. */
  lastMessageI18nKey?: string | null;
  /** What an attachment-only last message carried ('image' | 'video' |
   *  'audio' | 'file'), from get_workspace_inbox (20261001140000). Null when
   *  mixed or unknown; absent before that migration is applied. */
  lastMessageAttachmentKind?: string | null;
  /** The quest pays nothing (total_budget_usd = 0) — the inbox paints it rose. */
  isVolunteer?: boolean;
  /** The quest's content translations and source language, so read-only
   *  surfaces can show the title in the reader's language. */
  questI18n?: ContentI18n | null;
  questSourceLang?: string | null;
  /** The author's own title, kept by the inbox after questTitle is localized
   *  so search still matches the words the quest was written in. */
  questSourceTitle?: string;
}

// ─── On-chain tx log (admin dashboard) ──────────────────

export const ONCHAIN_CONTRACT_KEYS = [
  "questManager",
  "taskManager",
  "bountyBoard",
  "escrowVault",
  "userRegistry",
  "reputationLedger",
  "disputeResolver",
  "mockUSDC",
  "token",
] as const;
export type OnchainContractKey = (typeof ONCHAIN_CONTRACT_KEYS)[number];

export const ONCHAIN_TX_STATUSES = ["pending", "success", "reverted", "dropped"] as const;
export type OnchainTxStatus = (typeof ONCHAIN_TX_STATUSES)[number];

export interface OnchainLinkedWallet {
  address: string;
  walletClientType: string;
  chainType?: string;
}

export interface OnchainTxRow {
  attemptId: string;
  chainId: number;
  txHash: string | null;
  privyDid: string;
  uiResolvedSigner: string;
  storedWallet: string | null;
  registryWallet: string | null;
  linkedWallets: OnchainLinkedWallet[] | null;
  receiptFrom: string | null;
  contractKey: OnchainContractKey;
  method: string;
  initiatorFlow: string | null;
  relatedQuestId: string | null;
  relatedTaskId: string | null;
  sponsored: boolean;
  status: OnchainTxStatus;
  blockNumber: number | null;
  gasUsed: number | null;
  effectiveGasPrice: string | null;
  errorReason: string | null;
  submittedAt: string;
  confirmedAt: string | null;
}

export interface OnchainTxFilters {
  chainId: number;
  status?: OnchainTxStatus | "all";
  contractKey?: OnchainContractKey | "all";
  method?: string;
  addressPrefix?: string;
  hashSearch?: string;
  privyDid?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  cursor?: string;
}

export interface OnchainReceiptPatch {
  txHash?: string | null;
  receiptFrom?: string | null;
  status?: OnchainTxStatus;
  blockNumber?: number | null;
  gasUsed?: number | null;
  effectiveGasPrice?: string | null;
  errorReason?: string | null;
  confirmedAt?: string | null;
}

export interface OnchainTxInsert {
  attemptId: string;
  chainId: number;
  privyDid: string;
  uiResolvedSigner: string;
  storedWallet: string | null;
  registryWallet: string | null;
  linkedWallets: OnchainLinkedWallet[] | null;
  contractKey: OnchainContractKey;
  method: string;
  initiatorFlow: string | null;
  relatedQuestId: string | null;
  relatedTaskId: string | null;
  sponsored: boolean;
}

/**
 * What one tap on "I'm interested" actually did.
 *
 * `tasksAccepted` is what THIS call wrote, not the running total, so a re-tap
 * reports 0 and the UI can stay honest instead of claiming a fresh success.
 * `tasksSkipped` counts the quest's leaf tasks this call did not take — the
 * total is over ALL leaf tasks regardless of status, so this also includes
 * drafts, not only tasks already assigned, already settled, or already this
 * worker's.
 */
export interface ExpressInterestResult {
  candidateId: string;
  tasksAccepted: number;
  tasksSkipped: number;
}
